import { useState, useEffect } from 'react';
import { apiService } from '../lib/api';
import type { CompleteSuccessStory } from '../types';
import { HeroSection } from '../components/custom/HeroSection';
import { ProblemSection } from '../components/custom/ProblemSection';
import { SolutionSection } from '../components/custom/SolutionSection';
import { ImplementationSection } from '../components/custom/ImplementationSection';
import { ResultsSection } from '../components/custom/ResultsSection';
import { CtaSection } from '../components/custom/CtaSection';
import { Footer } from '../components/custom/Footer';
import { Navbar } from '../components/custom/Navbar';
import { AdminPanel } from '../components/custom/AdminPanel';
import { Loader2 } from 'lucide-react';

type ViewMode = 'public' | 'admin';

export default function Index() {
  const [viewMode, setViewMode] = useState<ViewMode>('public');
  const [story, setStory] = useState<CompleteSuccessStory | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDefaultStory();
  }, []);

  const loadDefaultStory = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPublishedStories();
      if (response.success && response.data.length > 0) {
        const firstStory = response.data[0];
        const completeStory = await apiService.getStoryById(firstStory.id);
        if (completeStory.success) {
          setStory(completeStory.data);
        }
      }
    } catch (error) {
      console.error('Error loading story:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStorySelect = async (storyId: string) => {
    try {
      setLoading(true);
      const response = await apiService.getStoryById(storyId);
      if (response.success) {
        setStory(response.data);
        setViewMode('public');
      }
    } catch (error) {
      console.error('Error loading story:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !story) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>);

  }

  if (viewMode === 'admin') {
    return (
      <div className="min-h-screen bg-background">
        <Navbar viewMode={viewMode} onViewModeChange={setViewMode} />
        <AdminPanel onStorySelect={handleStorySelect} onStoryUpdate={loadDefaultStory} />
      </div>);

  }

  if (!story) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar viewMode={viewMode} onViewModeChange={setViewMode} />
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-4xl font-bold text-foreground mb-4">No 11Success Stories Available000</h1>
          <p className="text-muted-foreground mb-8">
            Create your first customer success story in the admin panel.
          </p>
          <button
            onClick={() => setViewMode('admin')}
            className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity">

            Go to Admin Panel
          </button>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar viewMode={viewMode} onViewModeChange={setViewMode} />
      <HeroSection story={story} />
      <ProblemSection story={story} />
      <SolutionSection story={story} />
      <ImplementationSection story={story} />
      <ResultsSection story={story} />
      <CtaSection story={story} />
      <Footer />
    </div>);

}